<template>
	<div
		class="omapi-campaigns-table"
		:class="{
			'omapi-campaigns-table-refreshing': isRefreshing,
			'omapi-campaigns-table-no-data': !hasCampaigns,
		}"
	>
		<table-base :headers="headers" :striped="false" :is-loading="false" class="omapi-table">
			<template v-if="hasCampaigns">
				<campaigns-row
					v-for="campaign in campaigns"
					:key="campaign.id"
					:campaign-id="campaign.id"
					:class="`${rowClasses[campaign.id]}`"
					:row-visible="rowVisible(campaign)"
					:splits-open="splitsOpen(campaign)"
					@toggleAbTests="toggleAbTests"
				></campaigns-row>
			</template>

			<template #no-data>{{ noCampaignsError }}</template>
		</table-base>

		<div class="omapi-campaign-table__mods omapi-campaign-table__mods-bottom">
			<campaigns-table-bulk-actions
				:action-key="actionKey"
				:is-bulk="isBulk"
				@setAction="$emit('setAction', $event)"
			/>
			<campaigns-table-pagination />
		</div>

		<div v-for="campaign in campaigns" :key="campaign.id">
			<component :is="'campaigns-modal-' + modal" v-for="modal in modals" :key="modal" :campaign="campaign" />
		</div>

		<component :is="'campaigns-modal-' + modal" v-for="modal in bulkModals" :key="modal" />
	</div>
</template>

<script lang="js">
import { mapGetters, mapState } from 'vuex'
/**
 * Campaign Table
 * @displayName Campaign Table
 * @since 2.0.0
 */
export default  {
	inheritAttrs : false,
	props        : {
		/**
		 * Campaigns
		 */
		campaigns : {
			type    : Array,
			default : () => [],
		},
		/**
		 * Whether we are in bulk edit mode.
		 */
		isBulk : Boolean,
		/**
		 * The Bulk Action key
		 */
		actionKey : {
			type: String,
			default: '',
		},
		/**
		 * Whether campaigns are being refreshed.
		 */
		isRefreshing : Boolean,
	},
	data () {
		return {
			headers : [
				{ name: 'Name', class: 'campaign-table-name' },
				{ name: 'Stats', class: 'campaign-table-stats' },
				{ name: 'A/B Tests', class: 'campaign-table-ab' },
				{ name: 'Status', class: 'campaign-table-status' },
				{ name: '<span>Actions</span>', class: 'campaign-table-actions' },
			],
			modals : [
				'archive',
				'delete',
				'duplicate',
				'preview',
				'monster-link',
				'split',
				'trash',
			],
			bulkModals : [
				'bulk-archive',
				'bulk-delete',
				'bulk-trash',
			],
			abTestsToggled : [],
		}
	},
	computed : {
		...mapGetters([ 'hasSiteError' ]),
		...mapState('campaigns', [ 'bulk' ]),
		/**
		 * If there are any campaigns.
		 *
		 * @since  2.0.0
		 *
		 * @returns {boolean} If there are any campaigns.
		 */
		hasCampaigns () {
			return !this.hasSiteError && Boolean(this.campaigns && 0 < this.campaigns.length)
		},
		/**
		 * Object with a row class for every campaign row.
		 *
		 * @since  2.0.0
		 *
		 * @returns {boolean} Row classes object.
		 */
		rowClasses () {
			let lastStripe = ''
			const classes = {}
			this.campaigns.forEach(c => {
				// If not a split test, then toggle the stripe color.
				if (!c.is_split) {
					lastStripe = 'alternate' === lastStripe ? '' : 'alternate'
				}

				classes[c.id] = lastStripe
			})
			return classes
		},
		noCampaignsError () {
			return this.hasSiteError
				? 'An error was encountered.'
				: 'No campaign data.'
		},
	},
	methods : {
		/**
		 * Determines if row is hidden, due to being a split test and not
		 * yet toggled open.
		 *
		 * @since  2.0.0
		 *
		 * @param  {Object} campaign Campaign object.
		 *
		 * @returns {boolean} Whether row is hidden.
		 */
		rowVisible (campaign) {
			return campaign.is_split
				? this.abTestsToggled.includes(campaign.parent_id)
				: true
		},
		/**
		 * Determines if campaign's split tests are open.
		 *
		 * @since  2.0.0
		 *
		 * @param  {Object} campaign Campaign object.
		 *
		 * @returns {boolean} Whether campaign's split tests are open.
		 */
		splitsOpen (campaign) {
			return this.abTestsToggled.includes(campaign.id)
		},
		/**
		 * Toggle the A/B tests for a campaign.
		 *
		 * @param  {string} campaignId Campaign Id.
		 *
		 * @returns {void}
		 */
		toggleAbTests (campaignId) {
			const index = this.abTestsToggled.indexOf(campaignId)
			if (-1 !== index) {
				this.abTestsToggled.splice(index, 1)
			} else {
				this.abTestsToggled.push(campaignId)
			}
		},
	},
};
</script>

<style lang="scss">
@import '../../assets/scss/variables';
@import '../../assets/scss/components/campaign-table';
.omapi-campaigns-table {
	table.omapi-table {
		border: 0;

		thead th {
			border-color: #dddddd;
			padding-bottom: 12px;
			padding-top: 15px;
		}

		.alternate {
			background-color: #f7f8fb;
		}
	}
	.stripe-gray {
		background-color: #f9f9f9;
	}
}
.omapi-campaigns-table-refreshing {
	opacity: 0.5;
	&.omapi-campaigns-table-no-data {
		td {
			padding-top: 34px;
			padding-bottom: 33px;
		}
	}
}
.omapi-campaign-table__mods .bulk-actions {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-bottom: 12px;
	select {
		max-width: 168px;
		margin-right: 5px;
	}
	.omapi-dropdown__container {
		max-width: 168px;
	}
}
.omapi-campaign-table__mods-bottom .bulk-actions {
	margin-bottom: 0;
}
</style>
